import {Injectable} from '@angular/core';
import {HttpClient, HttpParams} from '@angular/common/http';
import {environment} from '@Env/environment';
import {DaoGeneric, ListParameters} from '@Services/base/daoService';
import {
  BlockForm,
  ControlTypeEnum,
  Field,
  FieldConfigName,
  FieldType,
  FormModel,
  FormType,
  OptionCommon
} from '../../components/form/models';
import {EMPTY, Observable, of} from 'rxjs';
import {Individu, IndividuExterne} from '@Common/core/services';
import {ResponseFormModel} from '../../components/form/models/response-form.model';
import {concatMap, switchMap} from 'rxjs/operators';
import {AbstractControl, ValidationErrors, ValidatorFn, Validators} from '@angular/forms';
import {IndividuDematService} from '../individus/individu-demat.service';
import {MatDialog} from '@angular/material/dialog';
import {Pagination} from '@Common/widgets/general/pagination';
import {ModalLinkFormComponent} from '../../../gestion/url-form/modal-link-form/modal-link-form.component';
import {ResponseCount} from '../../components/form/models/formdemat.model';
import {TypeDemandes} from '../../../gestion/demandes/formulaires-demandes-liste/type-demandes.enum';
import * as dateFnsAdaptor from 'date-fns';

@Injectable({ providedIn: 'root' })
export class FormulaireService extends DaoGeneric<FormModel> {
  private apiForm = `${environment.baseUrl}/demat/form_model/`;
  private apiCreateConceptionForm = `${environment.baseUrl}/demat/form_create_conception/`;
  private apiUpdateConceptionForm = `${environment.baseUrl}/demat/form_update_conception/`;
  private apiDefaultElements = `${environment.baseUrl}/demat/default_field/`;
  private apiResponseValue = `${environment.baseUrl}/demat/response_value/`;
  private apiFormsResponses = `${environment.baseUrl}/demat/response/responses_forms/`;
  private apiFormResponse = `${environment.baseUrl}/demat/form_model/form_response/`;
  private urlAttachements = `${environment.baseUrl}/demat/attachments/`;
  constructor(private httpClient: HttpClient,
              private dialog: MatDialog,
              private individuDematService: IndividuDematService) {
    super(httpClient);
  }

  getRootUrl(): string {
    return this.apiForm;
  }

  getFormById(idForm): Observable<any> {
    return this.httpClient.get<FormModel>(`${this.apiForm}${idForm}`);
  }

  sendResponseForm(response: ResponseFormModel) {
    return this.httpClient.post(this.apiResponseValue, response);
  }
  getDefaultElements(): Observable<FieldConfigName[]> {
    return this.httpClient.get<FieldConfigName[]>(this.apiDefaultElements);
  }

  saveElements(els: FieldConfigName[]): Observable<any> {
    return this.httpClient.post(this.apiDefaultElements, els);
  }
  /**
   * Obtient tous les formulaires (uniquement les données qui les décrivent)
   */
  getFormsSimple(filterParams: Map<string, string> = null): Observable<Pagination> {
    const url = `${this.apiForm}list_simple/`;
    const params = <ListParameters> {
      extraParams: filterParams,
      urlBaseOverride: url
    };
    return this.listItems(params);
  }
  duplicate(uuid: string, nom = null) {
    let params;
    if (nom) {
      params = { title: nom};
    }
    return this.httpClient.get<FormModel>(`${this.apiForm}${uuid}/duplicate/`, { params });
  }

  /**
   * Liste des formulaires avec leur comptage réponses
   * @param filterParams
   */
  getFormsResponsesCounts(filterParams: Map<string, string> = null): Observable<Pagination> {
    const url = `${this.apiForm}list_simple_with_count_responses/`;
    const params = <ListParameters> {
      extraParams: filterParams,
      urlBaseOverride: url
    };
    return this.listItems(params);
  }
  /**
   * Obtient l'url base vers le formulaire réponses
   * @private
   */
  private _getShortUrlBaseFormDemandeur() {
    return `/demandeur/formulaire/`;
  }
  private _getUrlBaseFormDemandeur() {
    const domain = `${location.protocol}//${location.hostname}${location.port ? `:${location.port}` : ''}`;
    const app = environment.env === 'dev' ? '' : '/demat';
    return `${domain}${app}${this._getShortUrlBaseFormDemandeur()}`;
  }

  /**
   * Le lien complet réponse demandeur
   * @param {string} slug
   */
  formUrlDemandeurBySlug(slug: string) {
    return `${this._getUrlBaseFormDemandeur()}${slug}`;
  }
  /**
   * le lien complet réponse demandeur
   * @param {FormModel} form
   */
  formUrlDemandeurByForm(form: FormModel) {
    return `${this._getUrlBaseFormDemandeur()}${form.url_obj[0]?.slug}`;
  }
  /**
   * Le lien réponse sans https://domain.ntld/
   * @param {string} slug
   */
  shortFormUrlDemandeurBySlug(slug: string) {
    return `${this._getShortUrlBaseFormDemandeur()}${slug}`;
  }
  /**
   * Le lien réponse préfixe, sans le slug
   */
  formUrlDemandeurBase() {
    return this._getUrlBaseFormDemandeur();
  }
  /**
   * Ouverture modale lien formulaire
   * @param form
   */
  openModaleUrlForm(form: FormModel) {
    const linkModale = this.dialog.open(ModalLinkFormComponent, {data: form});
    const top = 50;
    linkModale.updatePosition({top: `${top}px`});
  }
  /**
   * A partir d'un formulaire / d'une réponse formulaire / utilisateur, obtenir le formulaire avec les champs réponses
   * @param {string} formUUID : l'uuid du formulaire concerné
   * @param {string} responseUUID : la réponse dans ce formulaire
   * @param user : demandeur interne ou externe
   */
  formResponse(formUUID: string, responseUUID: string, user: Individu | IndividuExterne) {
    const params = {
      'form_uuid': formUUID,
      'response_uuid': responseUUID,
      'user_id': user.user
    };
    return this.httpClient.get<FormModel>(this.apiFormResponse, {params: params});
  }
  /**
   * Les formulaires réponses d'un connecté
   * @param {number} user : optionnel - par défaut, côté api,
   *   il prend selon le rôle : connecté si utilisateur lambda ou tous les formulaires si rôle admin / gestionnaire
   * @param {boolean} onlySurvey : optionnel - seulement les sondages ou tous les formulaires,
   */
  formsDemandeur(user: number = null, onlySurvey= false): Observable<FormModel[]> {
    const params = {};
    if (user) {
      params['user_user'] = user;
    }
    if (onlySurvey) {
      params['form__type'] = FormType.Sondage;
    }
    return this.httpClient.get<FormModel[]>(this.apiFormsResponses, {params});
  }

  private _formGestionnaire(formUUID: string, gestUID: string, action: string = 'add' || 'del'): Observable<FormModel> {
    const params = {
      'user_uid': gestUID
    };

    const url = `${environment.baseUrl}/demat/form_model/${formUUID}/gestionnaire_formulaire/`;
    if (action === 'del') {
      return this.httpClient.delete<FormModel>(url, {params: params});
    }
    if (action === 'add') {
      return this.individuDematService
        .getOrCreate(gestUID)
        .pipe(concatMap(_ind => this.httpClient.put<FormModel>(url, null, {params: params})));
    }
    return EMPTY;
  }
  /**
   * Ajout d'un gestionnaire à un formulaire
   * @param {FormModel} formModel
   * @param {string} gestUID : uuid individu à ajouter
   */
  addFormGestionnaire(formModel: FormModel, gestUID: string): Observable<FormModel> {
    if (! formModel.gestionnaires_obj || !(formModel.gestionnaires_obj.find(gest => gest.role.individu.uid === gestUID))) {
      /* send to backend form uuid and user uid to add gestionnaire and gestionnaire form */
      return this._formGestionnaire(formModel.uuid, gestUID, 'add');
    }
    return EMPTY;
  }

  delFormGestionnaire(formUUID: string, gestUID: string): Observable<FormModel> {
    return this._formGestionnaire(formUUID, gestUID, 'del');
  }
  fieldsSort(fields: Field[]) {
    fields.sort((_fieldA: Field, fieldB: Field) =>
      (_fieldA.options?.position > fieldB.options?.position) ? 1 : ((fieldB.options?.position > _fieldA.options?.position) ? -1 : 0));
  }

  /**
   * Obtient l'équivalent ControlTypeEnum à partir d'une valeur FieldType pour le filtrage des réponses
   * @param {FieldType} type
   * @return {ControlTypeEnum} value
   */
  getFieldTypeToControlTypeEnum(type: FieldType): ControlTypeEnum {
    const types: Map<FieldType, ControlTypeEnum> = new Map<FieldType, ControlTypeEnum>([
      [FieldType.TypeText, ControlTypeEnum.Text],
      [FieldType.TypeTextarea, ControlTypeEnum.TextArea],
      [FieldType.TypeDate, ControlTypeEnum.Date],
      [FieldType.TypeRadio, ControlTypeEnum.RadioButton],
      [FieldType.TypeCheckbox, ControlTypeEnum.Checkbox],
      [FieldType.TypeSelect, ControlTypeEnum.ComboList],
      [FieldType.TypePhone, ControlTypeEnum.Phone],
      [FieldType.TypeEmail, ControlTypeEnum.Email],
      [FieldType.TypeFile, ControlTypeEnum.File],
      [FieldType.TypeSvcCmp, ControlTypeEnum.SvcCmp]
    ]);
    return types.get(type) || null;
  }
  /**
   * Reconstruction des "fields" pour la modification d'un formulaire du fields nécessaire pour l'écriture du formulaire en back
   * @param {BlockForm} block : le bloc concerné, doit contenir fields_obj des champs déjà contruits
   */
  constructFieldsConfig(block: BlockForm): BlockForm {
    if (block.fields_objs) {
      this.fieldsSort(block.fields_objs);
      block.fields = block.fields_objs.map(_field_obj =>
        <FieldConfigName>{
          id: _field_obj.field,
          defaultFieldId: _field_obj.field,
          options: _field_obj.options_obj,
          position: _field_obj.options?.position
        });
    }
    return block;
  }

  /**
   * API spécifique pour la création de la conception d'un formulaire avec ses blocs et champs
   * @param {FormModel} form : le formulaire à créer avec ses blocs et champs
   */
  createConceptionForm(form: FormModel) {
    return this.httpClient.post(this.apiCreateConceptionForm, form);
  }

  /**
   * API spécifique pour la modification de la conception d'un formulaire avec ses blocs et champs
   * @param {FormModel} form : le formulaire à modifier avec ses blocs et champs
   */

  updateConceptionForm(form: FormModel) {
    const url = `${this.apiUpdateConceptionForm}${form.uuid}/`;
    if (form.attachments && form.attachments.length > 0) {
      return this.uploadFiles(form.attachments, form.id).pipe(
        switchMap(() => this.httpClient.put(url, form))
      );
    } else {
      return this.httpClient.put(url, form);
    }
  }

  uploadFiles(filesToUpload, formId: number) {
    const url = `${environment.baseUrl}/demat/attachments/`;
    const formData = new FormData();

    if (filesToUpload && filesToUpload.length > 0) {
      filesToUpload.forEach((file) => {
        if (file instanceof File) {
          formData.append('files', file, file.name);
        }
      });

      formData.append('form', formId.toString());

      return this.httpClient.post(url, formData);
    }

    return of(null);
  }

  deleteFile(fileId: number) {
    const url = `${this.urlAttachements}${fileId}/`;
    return this.httpClient.delete(url);
  }

  downloadFile(urlPath: string, idOrUuid: string, isForm = false) {
    const base = isForm ? this.apiForm : this.urlAttachements;
    const url = `${base}${idOrUuid}${urlPath}`;
    return this.httpClient.get<Blob>(url, {responseType: 'blob' as 'json'});
  }

  downloadAttachement(file) {
    return this.downloadFile('/get_attachement/', file.id);
  }

  exportAttachementsForm(form: FormModel) {
    return this.downloadFile('/export_attachments/', form.uuid, true);
  }

  exportPdfForm(form: FormModel) {
    return this.downloadFile('/export_form_pdf/', form.uuid, true);
  }

  private _countByTypeDemande(responsesCount: ResponseCount, typeDemande: TypeDemandes) {
    switch (typeDemande) {
      case TypeDemandes.Incomplet:
        return responsesCount.count_incomplet;
      case TypeDemandes.En_traitement:
        return responsesCount.count_traitement;
      case TypeDemandes.En_attente:
        return responsesCount.count_waiting;
      case TypeDemandes.Traitees:
        return responsesCount.count_traite;
      case TypeDemandes.Archivees:
        return responsesCount.count_archive;
      default:
        return 0;
    }
  }
  /**
   * State_response :
   *    1 (brouillon) et last instruction 4 : incomplet
   *    2 (publié) et last instruction null : en attente
   *    2 (publié) et last instruction 0 : en traitement
   *    2 (publié) et last instruction 1 / 2 / 3 = accepté | refusé | sans suite : traité
   *    3 (archivé) : archivé
   * @param {ResponseCount[]} responsesCount
   * @param {TypeDemandes} typeDemande
   */
  public calculateCountResponseByState(responsesCount: ResponseCount[], typeDemande: TypeDemandes) {
    let count = 0;
    responsesCount.forEach(responseCount => count = this._countByTypeDemande(responseCount, typeDemande));
    return count;
  }
  /**
   * Françisation des états, avec pluriel ou non
   */
  public getLabelStatePlural(state: TypeDemandes) {
    const stateLabel: Map<TypeDemandes, any> = new Map<TypeDemandes, any>([
      [TypeDemandes.En_attente, {'=0': 'en attente', '=1': 'en attente', 'other': 'en attente'}],
      [TypeDemandes.En_traitement, {'=0': 'en traitement', '=1': 'en traitement', 'other': 'en traitement'}],
      [TypeDemandes.Archivees, {'=0': 'archivée', '=1': 'archivée', 'other': 'archivées'}],
      [TypeDemandes.Incomplet, {'=0': 'incomplète', '=1': 'incomplète', 'other': 'incomplètes'}],
      [TypeDemandes.Traitees, {'=0': 'traitée', '=1': 'traitée', 'other': 'traitées'}]
    ]);
    return stateLabel.get(state) || '';
  }
  /**
   * Validators : Email, Téléphone, ...
   * @param {OptionCommon} fieldOption
   */
  public genericFieldValidator(fieldOption: OptionCommon): ValidatorFn[] {
    // Email, utilise la ValidatorFn standard
    if (fieldOption.controlType === FieldType.TypeEmail) {
      return [
        Validators.email,
      ];
    }
    // Phone, utilise une ValidatorFn propre
    if (fieldOption.controlType === FieldType.TypePhone) {
      const validators: ValidatorFn[] = [
        (control: AbstractControl): ValidationErrors | null => {
          if (control.value && !(/^0[1-9]\d{8}$/i.test(control.value))) {
            return {phone: {value: 'wrong_number'}};
          }
          return null;
        },
      ];
      if (fieldOption.isRequired) {
        validators.push(
          Validators.required,
        );
      }
      return validators;
    }
    // Checkbox, vérife qu'au moins une box est coche quand le champ est obligatoire
    if (fieldOption.controlType === FieldType.TypeCheckbox && fieldOption.isRequired) {
      return [
        (control: AbstractControl): ValidationErrors | null => {
          let once = false;
          control.value.forEach((obj) => {
            if (obj.value) {
              once = true;
            }
          });
          return (once) ? null : {required: {value: 'required'}};
        },
      ];
    }
    // Radio, vérife qu'au moins une radio est coche quand le champ est obligatoire
    if (fieldOption.controlType === FieldType.TypeRadio && fieldOption.isRequired) {
      return [
        (control: AbstractControl): ValidationErrors | null => {
          return (control.value) ? null : {required: {value: 'required'}};
        },
      ];
    }
    // Select/Combolist, vérife qu'au moins une entrée est sélectionner quand le champ est obligatoire
    if (fieldOption.controlType === FieldType.TypeSelect && fieldOption.isRequired) {
      return [
        (control: AbstractControl): ValidationErrors | null => {
          return (control.value) ? null : {required: {value: 'required'}};
        },
      ];
    }
    // ValidatorFn par defaut
    if (fieldOption.isRequired) {
      return [
        Validators.required,
      ];
    } else {
      return [
        (control: AbstractControl): ValidationErrors | null => {
          return null; /* is valid */
        },
      ];
    }
  }
  formatDateToSql(date, format = 'yyyy-MM-dd') {
    if (date) {
      return dateFnsAdaptor.format(new Date(date), format);
    }
    return null;
  }

  /**
   * Ajoute ou supprime une liste de sollicités à un formulaire
   * @param {string} action : action à opérater : ajouter ou supprimer la liste au formulaire
   * @param {string} formUUID : le formulaire sur lequel rattacer la liste
   * @param {number} listId : ID liste sollicité précédemment créée
   */
  addOrDeleteListRequestersToForm(action: 'add' | 'delete', formUUID: string, listId: number) {
    const _url = `${this.apiForm}${formUUID}/list_requesters/`;
    const params = this._getUrlHttpParams(new Map([['list_id', listId.toString()]]));
    const action_add_delete = {
      add: this.httpClient.patch(`${_url}`, null, {params: params}),
      delete: this.httpClient.delete(`${_url}`, {params: params}),
    };
    return action_add_delete[action];
  }
}
